import { useToast, LoadingPage } from "@qubit/autoparts";
import * as Sentry from "@sentry/react";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { warehouseService } from "~/api/warehouse";
import { setSignalREnabled } from "~/app/app.slice";
import { useAppSelector, useAppDispatch } from "~/app/store";
import { selectHandRaisedForWorkstation } from "~/features/andon/andon.selectors";
import {
  clearEventData,
  clearAndonSessionData
} from "~/features/andon/andon.slice";
import { useCloseWorkstationWithErrorToast } from "~/hooks/useCloseWorkstationWithErrorToast";
import { mixpanelLogout } from "~/lib/mixpanel-tracking";
import { sendUserEventAction } from "~/redux/actions/userEvents";
import { customClientBApi } from "~/redux/customClientB/customClientBApi";
import { publicApi } from "~/redux/public/publicApi";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useToggleHandRaiseMutation } from "~/redux/warehouse/andon.hooks";
import { warehouseApi } from "~/redux/warehouse/warehouseApi";

import HandRaiseReminderModal from "./HandRaiseReminderModal";
import { LogoutFailedDialog } from "./components/LogoutFailedDialog";
import { clearLoginState, setUnauthorizedLogoutRequested } from "./login.slice";
import {
  type AuthLogoutContext,
  isNonFederatedAuth,
  performAuthLogout,
  reportFederatedLogoutFailure,
  reportSpaLoginFallback
} from "./login.utils";

export const LogoutV2 = () => {
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const navigate = useNavigate();

  const closeWorkstationWithErrorToast = useCloseWorkstationWithErrorToast();
  const [toggleHandRaise] = useToggleHandRaiseMutation();

  const [showHandPrompt, setShowHandPrompt] = useState(false);
  const [continueLogout, setContinueLogout] = useState(false);
  const [logoutFailed, setLogoutFailed] = useState(false);

  const loginPath =
    useAppSelector((state) => state.app.siteLoginPath) || "/login";
  const customLoginPath = useAppSelector((state) => state.site.customLoginPath);
  const authMethod = useAppSelector((state) => state.login.authMethod);
  const isUserLoggedIn = useAppSelector((state) => state.login.isUserLoggedIn);
  const siteWorkstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const selectedAutostoreGridId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.autostoreGridId
  );
  const handRaised = useAppSelector((state) => {
    return siteWorkstationId
      ? selectHandRaisedForWorkstation(siteWorkstationId)(state)
      : { raised: false, timestamp: null };
  });
  const unauthorizedLogoutRequested = useAppSelector(
    (state) => state.login.unauthorizedLogoutRequested
  );

  const authContextRef = useRef<AuthLogoutContext | null>(null);
  const hasLoggedOut = useRef(false);

  const finishAuthRedirect = useCallback(async () => {
    const ctx = authContextRef.current;
    if (!ctx) return;

    if (isNonFederatedAuth(ctx.authMethod)) {
      navigate(ctx.loginPath);
      return;
    }

    const success = await performAuthLogout(ctx);
    if (!success) {
      reportFederatedLogoutFailure(ctx);
      setLogoutFailed(true);
    }
  }, [navigate]);

  useEffect(() => {
    const handleLogout = async () => {
      if (hasLoggedOut.current) {
        return;
      }
      hasLoggedOut.current = true;
      authContextRef.current = { authMethod, customLoginPath, loginPath };
      dispatch(setUnauthorizedLogoutRequested(false));

      try {
        Sentry.addBreadcrumb({
          message: "logout: clear-andon-data",
          level: "info"
        });
        dispatch(clearEventData());
        dispatch(clearAndonSessionData());

        if (usersFulfillmentCenter) {
          Sentry.addBreadcrumb({
            message: "logout: close-workstation-ports",
            level: "info"
          });
          await closeWorkstationWithErrorToast();
        } else {
          Sentry.addBreadcrumb({
            message:
              "logout: skip-close-workstation-ports (no fulfillment center)",
            level: "warning"
          });
        }

        try {
          Sentry.addBreadcrumb({
            message: "logout: send-user-logged-out-event",
            level: "info"
          });
          await dispatch(sendUserEventAction("UserLoggedOut"));
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "user-event-logout" },
            extra: { event: "UserLoggedOut" }
          });
          errorToast("Failed to send user event action");
        }

        Sentry.addBreadcrumb({
          message: "logout: deactivate-workstation",
          level: "info",
          data: { workstationId: siteWorkstationId ?? "none" }
        });
        if (siteWorkstationId) {
          try {
            await warehouseService.post(
              `/api/workstations/${siteWorkstationId}/deactivate-workstation`
            );
          } catch (err) {
            Sentry.captureException(err, {
              tags: { feature: "workstation-deactivate-logout" },
              extra: { workstationId: siteWorkstationId }
            });
          }
        }

        Sentry.addBreadcrumb({
          message: "logout: mixpanel",
          level: "info"
        });
        mixpanelLogout();

        Sentry.addBreadcrumb({
          message: "logout: clear-login-state",
          level: "info"
        });
        try {
          dispatch(clearLoginState());
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "logout-clear-state" }
          });
        }

        Sentry.addBreadcrumb({
          message: "logout: disable-signalr-and-reset-api-state",
          level: "info"
        });
        try {
          dispatch(setSignalREnabled(false));
          dispatch(warehouseApi.util.resetApiState());
          dispatch(publicApi.util.resetApiState());
          dispatch(customClientBApi.util.resetApiState());
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: "logout-reset-api" }
          });
        }

        Sentry.captureMessage("logout: all steps completed", {
          level: "info",
          tags: { feature: "logout-success" }
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: "logout-unhandled" }
        });
      }

      await finishAuthRedirect();
    };

    if (isUserLoggedIn) {
      if (
        !unauthorizedLogoutRequested &&
        handRaised.raised &&
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        !continueLogout
      ) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
        setShowHandPrompt(true);
        return;
      }
      void handleLogout();
    } else if (!hasLoggedOut.current) {
      navigate(loginPath);
    }
  }, [
    dispatch,
    closeWorkstationWithErrorToast,
    usersFulfillmentCenter,
    errorToast,
    customLoginPath,
    loginPath,
    navigate,
    authMethod,
    isUserLoggedIn,
    handRaised,
    continueLogout,
    unauthorizedLogoutRequested,
    siteWorkstationId,
    finishAuthRedirect
  ]);

  const handleRetry = useCallback(() => {
    setLogoutFailed(false);
    void finishAuthRedirect();
  }, [finishAuthRedirect]);

  const handleGoToLogin = useCallback(() => {
    const ctx = authContextRef.current;
    if (ctx) {
      reportSpaLoginFallback(ctx);
      navigate(ctx.loginPath);
    } else {
      navigate(loginPath);
    }
    setLogoutFailed(false);
  }, [loginPath, navigate]);

  return (
    <>
      <LoadingPage loadingMessage={t("logging off")} />
      {showHandPrompt && (
        <HandRaiseReminderModal
          variant="logout"
          onLowerHand={async () => {
            if (selectedAutostoreGridId && siteWorkstationId) {
              try {
                await toggleHandRaise({
                  autostoreGridId: selectedAutostoreGridId,
                  workstationId: siteWorkstationId,
                  raiseHand: false
                });
              } catch {
                errorToast("Failed to lower hand before logout");
              }
            }
            setContinueLogout(true);
            setShowHandPrompt(false);
          }}
          onKeepHandRaised={() => {
            setContinueLogout(true);
            setShowHandPrompt(false);
          }}
        />
      )}
      <LogoutFailedDialog
        open={logoutFailed}
        onRetry={handleRetry}
        onGoToLogin={handleGoToLogin}
      />
    </>
  );
};
